import { useQuery } from '@tanstack/react-query'
import { DatePicker, Flex, Select, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  SeverityEnum,
  organizationsAuditLogsListOptions,
  type AuditLogList,
  type OrganizationsAuditLogsListData
} from 'src/api/main'
import {
  ActiveFilterTags,
  BadgeText,
  AuditActionTag,
  DateTimeCell,
  FilterSelect,
  MoreFilters,
  ProTableContainer,
  QueryErrorAlert,
  SeverityDot
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider } from 'src/context'
import {
  useFacetFilter,
  useQueryParams,
  useQueryUpdate,
  useSessionContext
} from 'src/hooks'
import {
  SEVERITY_COLORS,
  SEVERITY_TAG_COLOR,
  defaultFilters,
  getAuditEventCode,
  humanizeAuditCode,
  normalizeAuditSeverity,
  toAuditAcceptLanguage,
  translateAuditAction,
  translateAuditSeverity
} from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

const { Text } = Typography
const { RangePicker } = DatePicker

type SeverityFilter = SeverityEnum | 'all'

type AuditListQuery = NonNullable<OrganizationsAuditLogsListData['query']>

type DecoratedEvent = AuditLogList & {
  severityValue: SeverityEnum | null
  eventTypeLabel: string | null
  descriptionLabel: string
}

const normalizeLabel = (value: string) =>
  value.replace(/[^A-Za-z0-9]+/g, '').toLowerCase()

const getTranslatedAuditLabel = (t: TFunction, code: string) =>
  code ? translateAuditAction(t, code) : ''

const getDescriptionLabel = (event: AuditLogList, t: TFunction) => {
  const eventCode = getAuditEventCode(event)
  const translatedLabel = getTranslatedAuditLabel(t, eventCode)
  const humanizedLabel = eventCode ? humanizeAuditCode(eventCode) : ''
  const description = event.description?.trim()

  if (!description) return translatedLabel || humanizedLabel

  return humanizedLabel &&
    normalizeLabel(description) === normalizeLabel(humanizedLabel)
    ? translatedLabel || description
    : description
}

const decorateEvent = (event: AuditLogList, t: TFunction): DecoratedEvent => ({
  ...event,
  severityValue: normalizeAuditSeverity(event.severity),
  eventTypeLabel: event.event_type
    ? getTranslatedAuditLabel(t, event.event_type)
    : null,
  descriptionLabel: getDescriptionLabel(event, t)
})

/**
 * The signed-in user's own audit trail, scoped to their active workspace. Reads
 * the shared organization audit-log feed filtered by `actor_id` (the personal
 * `/account/audit-logs/` endpoint was retired in favour of this reader), so it
 * mirrors `UserAuditTab`'s data shape — minus the actor column, which is always
 * the current user here.
 */
const AccountAuditPanelBase = () => {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const acceptLanguage = toAuditAcceptLanguage(language)

  const { user, activeWorkspaceId } = useSessionContext()
  const actorId = user?.user.id ?? 0
  const organizationId = activeWorkspaceId ?? 0

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const serviceFilter = String(params.service ?? '')
  const eventTypeFilter = String(params.event_type ?? '')
  const severityFilter =
    (params.severity as SeverityFilter | undefined) ?? 'all'
  const fromFilter = String(params.from ?? '')
  const toFilter = String(params.to ?? '')

  const enabled = organizationId > 0 && actorId > 0

  const auditQuery = useMemo<AuditListQuery>(
    () => ({
      actor_id: String(actorId),
      limit: perPage,
      offset: (page - 1) * perPage,
      search: searchTerm.trim() || undefined,
      service: serviceFilter || undefined,
      event_type: eventTypeFilter || undefined,
      severity: severityFilter === 'all' ? undefined : severityFilter,
      timestamp_gte: fromFilter || undefined,
      timestamp_lte: toFilter || undefined
    }),
    [
      actorId,
      perPage,
      page,
      searchTerm,
      serviceFilter,
      eventTypeFilter,
      severityFilter,
      fromFilter,
      toFilter
    ]
  )

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: organizationId },
      query: auditQuery,
      headers: { 'Accept-Language': acceptLanguage }
    }),
    enabled,
    placeholderData: (previousData) => previousData,
    // Opt out of the app-wide refetchOnMount:false. New events land constantly, so
    // re-entering the page must hit the backend instead of rendering the old page.
    refetchOnMount: 'always',
    // Audit rows are append-only and land constantly: also re-fetch when the user
    // comes back to the browser tab, since a mount-only refetch misses the case
    // where the action was performed elsewhere and this page stayed open.
    refetchOnWindowFocus: true
  })

  const rows = useMemo(
    () => (data?.results ?? []).map((event) => decorateEvent(event, t)),
    [data?.results, t]
  )
  const total = data?.count ?? 0

  // Options come from the org-wide `/filters` facet, not a sample of the user's
  // recent events: sampling would drop any service/type used only outside the
  // sample window (e.g. 100 actions in one app today would hide yesterday's
  // other apps). The facet lists the complete set — an option with no rows for
  // this user is harmless, missing one is not.
  const service = useFacetFilter({
    organizationId,
    field: 'service',
    selected: serviceFilter,
    getLabel: humanizeAuditCode,
    enabled
  })
  const eventTypeLabel = useCallback(
    (value: string) => getTranslatedAuditLabel(t, value),
    [t]
  )
  const eventType = useFacetFilter({
    organizationId,
    field: 'event_type',
    selected: eventTypeFilter,
    getLabel: eventTypeLabel,
    enabled
  })

  const rangeValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const fromDay = fromFilter ? dayjs(fromFilter) : null
    const toDay = toFilter ? dayjs(toFilter) : null

    return fromDay || toDay ? [fromDay, toDay] : null
  }, [fromFilter, toFilter])

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'service',
      label: t('account.audit.filters.allApplications', {
        defaultValue: 'Application'
      })
    },
    {
      type: 'select',
      key: 'event_type',
      label: t('account.audit.filters.allEventTypes', {
        defaultValue: 'Event type'
      })
    },
    {
      type: 'select',
      key: 'severity',
      label: t('account.audit.filters.allSeverities', {
        defaultValue: 'Severity'
      })
    },
    {
      type: 'dateRange',
      key: 'from',
      from: 'from',
      to: 'to',
      label: t('account.audit.dateRangeLabel', { defaultValue: 'Date range' }),
      clearKeys: ['from', 'to']
    }
  ]

  const moreFiltersActiveCount = [
    severityFilter === 'all' ? '' : severityFilter,
    fromFilter || toFilter
  ].filter((value) => value !== '').length

  const resetMoreFilters = () =>
    setFilter({
      severity: null,
      from: null,
      to: null
    })

  const clearFilters = () => {
    updateQuery({}, true)
  }

  const handleRangeChange = (next: [Dayjs | null, Dayjs | null] | null) => {
    setFilter({
      from: next?.[0] ? next[0].startOf('day').toISOString() : null,
      to: next?.[1] ? next[1].endOf('day').toISOString() : null
    })
  }

  const columns: ColumnsType<DecoratedEvent> = useMemo(
    () => [
      {
        title: '',
        key: 'severity_dot',
        fixed: 'left',
        width: 24,
        align: 'center',
        render: (_v, record) => <SeverityDot severity={record.severityValue} />
      },
      {
        title: t('account.audit.columns.time', { defaultValue: 'Time' }),
        dataIndex: 'timestamp',
        key: 'timestamp',
        fixed: 'left',
        width: 150,
        render: (value: string) => (
          <DateTimeCell
            value={value}
            variant="relative"
            style={{ fontSize: 12 }}
          />
        )
      },
      {
        title: t('account.audit.columns.event', { defaultValue: 'Event' }),
        key: 'event',
        width: 180,
        render: (_v, record) =>
          record.eventTypeLabel ? (
            <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
              {record.eventTypeLabel}
            </Tag>
          ) : (
            <AuditActionTag action={getAuditEventCode(record)} maxWidth={165} />
          )
      },
      {
        title: t('account.audit.columns.application', {
          defaultValue: 'Application'
        }),
        dataIndex: 'application_name',
        key: 'application',
        width: 160,
        render: (name: string | null, record) => {
          const label = name || humanizeAuditCode(record.service)

          return label ? (
            <Text style={{ fontSize: 12 }}>{label}</Text>
          ) : (
            <Text type="secondary">—</Text>
          )
        }
      },
      {
        title: t('account.audit.columns.description', {
          defaultValue: 'Description'
        }),
        dataIndex: 'descriptionLabel',
        key: 'description',
        width: 200,
        ellipsis: { showTitle: false },
        render: (description: string) =>
          description ? (
            <Tooltip placement="topLeft" title={description}>
              <Text
                ellipsis
                style={{ display: 'block', fontSize: 12, maxWidth: 200 }}
              >
                {description}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('account.audit.columns.severity', {
          defaultValue: 'Severity'
        }),
        dataIndex: 'severityValue',
        key: 'severity',
        width: 110,
        render: (severity: SeverityEnum | null) =>
          severity ? (
            <Tag
              color={SEVERITY_TAG_COLOR[severity]}
              style={{
                marginInlineEnd: 0,
                fontSize: 10,
                textTransform: 'capitalize'
              }}
            >
              {translateAuditSeverity(t, severity)}
            </Tag>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('account.audit.columns.ip', { defaultValue: 'IP' }),
        dataIndex: 'client_ip',
        key: 'ip',
        width: 140,
        render: (ip: string | null) =>
          ip ? (
            <Text code style={{ fontSize: 11 }}>
              {ip}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )
      }
    ],
    [t]
  )

  const filtersDirty =
    searchTerm !== '' ||
    serviceFilter !== '' ||
    eventTypeFilter !== '' ||
    severityFilter !== 'all' ||
    fromFilter !== '' ||
    toFilter !== ''

  if (isError) {
    return <QueryErrorAlert onRetry={() => void refetch()} />
  }

  return (
    <ProTableContainer<DecoratedEvent>
      tableIdentifier="account-audit"
      searchPlaceholder={t('account.audit.searchPlaceholder', {
        defaultValue: 'Search events'
      })}
      rowKey="id"
      columns={columns}
      data={{ count: total, results: rows }}
      loading={{ spinning: isLoading || isFetching, delay: 200 }}
      showSorterTooltip={false}
      locale={{
        emptyText: filtersDirty
          ? t('account.audit.noMatches', {
              defaultValue: 'No matching events'
            })
          : t('account.audit.empty')
      }}
      leftChildren={
        <>
          <FilterSelect
            value={serviceFilter}
            options={service.options}
            onSelect={(value) => setFilter({ service: value || null })}
            filterKey="service"
            onSearch={service.onSearch}
            placeholder={t('account.audit.filters.allApplications', {
              defaultValue: 'Application'
            })}
            width={FILTER_CONTROL_WIDTH}
          />
          <FilterSelect
            value={eventTypeFilter}
            options={eventType.options}
            onSelect={(value) => setFilter({ event_type: value || null })}
            filterKey="event_type"
            onSearch={eventType.onSearch}
            placeholder={t('account.audit.filters.allEventTypes', {
              defaultValue: 'Event type'
            })}
            width={FILTER_CONTROL_WIDTH}
          />
          <MoreFilters
            filterActiveCount={moreFiltersActiveCount}
            onResetMoreFilters={resetMoreFilters}
            buttonProps={{ size: 'small' }}
          >
            <MoreFilters.Col
              title={t('account.audit.filters.allSeverities', {
                defaultValue: 'Severity'
              })}
            >
              <Select<SeverityFilter>
                size="small"
                allowClear
                placeholder={t('account.audit.filters.allSeverities', {
                  defaultValue: 'Severity'
                })}
                value={severityFilter === 'all' ? undefined : severityFilter}
                onChange={(value) => setFilter({ severity: value ?? null })}
                style={{ width: '100%' }}
                options={[
                  {
                    value: SeverityEnum.LOW,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.LOW)}
                        color={SEVERITY_COLORS[SeverityEnum.LOW]}
                      />
                    )
                  },
                  {
                    value: SeverityEnum.MEDIUM,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.MEDIUM)}
                        color={SEVERITY_COLORS[SeverityEnum.MEDIUM]}
                      />
                    )
                  },
                  {
                    value: SeverityEnum.HIGH,
                    label: (
                      <BadgeText
                        text={translateAuditSeverity(t, SeverityEnum.HIGH)}
                        color={SEVERITY_COLORS[SeverityEnum.HIGH]}
                      />
                    )
                  }
                ]}
              />
            </MoreFilters.Col>

            <MoreFilters.Col
              title={t('account.audit.dateRangeLabel', {
                defaultValue: 'Date range'
              })}
            >
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                value={rangeValue}
                onChange={(next) =>
                  handleRangeChange(next as [Dayjs | null, Dayjs | null] | null)
                }
                placeholder={[
                  t('account.audit.filters.startDate', {
                    defaultValue: 'Start date'
                  }),
                  t('account.audit.filters.endDate', {
                    defaultValue: 'End date'
                  })
                ]}
                aria-label={t('account.audit.dateRangeLabel', {
                  defaultValue: 'Date range'
                })}
              />
            </MoreFilters.Col>
          </MoreFilters>

          <Flex flex="1 0 100%">
            <ActiveFilterTags
              items={filterTagItems}
              onClearAll={filtersDirty ? clearFilters : undefined}
            />
          </Flex>
        </>
      }
      rightChildren={
        <Flex align="center" gap={8}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('account.audit.toolbar.events', {
              count: total,
              defaultValue: '{{count}} events'
            })}
          </Text>
        </Flex>
      }
    />
  )
}

export const AccountAuditPanel = () => (
  <FilterLabelsProvider>
    <AccountAuditPanelBase />
  </FilterLabelsProvider>
)
